import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Zap } from '@/components/ui/icons';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { UpgradeModal } from '@/components/UpgradeModal';
import { useIsMobile } from '@/hooks/useIsMobile';
import { getLevel, useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

function formatCompact(n: number): string {
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return `${v >= 10 ? v.toFixed(0) : v.toFixed(1)}M`;
  }
  if (n >= 1_000) {
    const v = n / 1_000;
    return `${v >= 100 ? v.toFixed(0) : v.toFixed(1)}K`;
  }
  return n.toLocaleString();
}

function formatFull(n: number): string {
  return n.toLocaleString();
}

function formatPeriodEnd(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function CreditsButton() {
  const isMobile = useIsMobile();
  const { user, billing } = useAuth();
  const level = getLevel(billing);
  const totalTokens = billing?.tokens.total ?? 0;
  const freeTokens = billing?.tokens.free ?? 0;
  const subscriptionTokens = billing?.tokens.subscription ?? 0;
  const purchasedTokens = billing?.tokens.purchased ?? 0;
  const periodEnd = formatPeriodEnd(
    billing?.subscription?.currentPeriodEnd ?? null,
  );

  const [open, setOpen] = useState(false);
  // Card opened by click stays pinned — mouse-leave alone won't dismiss it.
  // Only outside-click (or an explicit close action) clears this.
  const [pinnedByClick, setPinnedByClick] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [cardStyle, setCardStyle] = useState<React.CSSProperties>({});
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Compute fixed position from trigger rect whenever the card opens
  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setCardStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, [open]);

  // Close when clicking outside while open (dismisses both hover + click open states)
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (e.target instanceof Node && wrapperRef.current?.contains(e.target)) {
        return;
      }
      setOpen(false);
      setPinnedByClick(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Clear any in-flight hover timers when the component unmounts (e.g. the
  // user navigates away from `/` while a 300ms open-delay is still pending).
  useEffect(() => {
    return () => {
      if (openTimer.current) window.clearTimeout(openTimer.current);
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    };
  }, []);

  // Drop any pinned/hover state when crossing into the mobile renderer so
  // resizing back to desktop doesn't re-surface a stale popover.
  useEffect(() => {
    if (!isMobile) return;
    setOpen(false);
    setPinnedByClick(false);
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, [isMobile]);

  if (!user) return null;

  if (isMobile) {
    return (
      <Link
        to="/subscription"
        aria-label="View credits"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full',
          'bg-telekinesis-neutral-950 px-3 py-2 text-sm font-medium',
          'text-telekinesis-neutral-10 shadow-sm',
          'border border-white/5',
        )}
      >
        <Zap className="mt-0.5 h-3.5 w-3.5 self-start" fill="currentColor" />
        <div className="flex flex-col items-start leading-tight">
          <span>{formatCompact(totalTokens)} Tokens</span>
          <span className="text-xs text-[#107d40] underline">Get More</span>
        </div>
      </Link>
    );
  }

  const isFree = level === 'free';
  const headerLabel = isFree
    ? 'Daily credits'
    : periodEnd
      ? `Renews ${periodEnd}`
      : 'Current credits';

  const handleEnter = () => {
    // Hover no longer OPENS the card — it opens on click only (handlePillClick).
    // Previously a 300ms hover-intent timer popped the card open, so merely
    // resting the cursor over the pill (or an automation cursor parked there)
    // left the account card "stuck open". We still cancel any pending close so
    // moving between the pill and an already-open (click-pinned) card doesn't
    // flicker it shut.
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleLeave = () => {
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    // If the user explicitly clicked to pin the card, don't auto-close on
    // mouse-leave — they'll dismiss it themselves via outside-click.
    if (pinnedByClick) return;
    // small delay so moving between pill and card doesn't flicker the card closed
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  const handlePillClick = () => {
    // Click toggles the card open/closed (and pins it so it stays until the
    // user dismisses it via another click or an outside click).
    const next = !open;
    setOpen(next);
    setPinnedByClick(next);
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  return (
    <>
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
      <div
        ref={wrapperRef}
        className="relative"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        <button
          ref={buttonRef}
          type="button"
          onClick={handlePillClick}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="View credits"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full',
            'bg-telekinesis-neutral-950 px-3 py-2 text-sm font-medium',
            'text-telekinesis-neutral-10 shadow-sm',
            'border border-white/5',
            'transition-colors',
            '[@media(hover:hover)]:hover:bg-telekinesis-neutral-900',
          )}
        >
          <Zap className="mt-0.5 h-3.5 w-3.5 self-start" fill="currentColor" />
          <div className="flex flex-col items-start leading-tight">
            <span>{formatCompact(totalTokens)} Tokens</span>
            <span className="text-xs text-[#107d40] underline">Get More</span>
          </div>
        </button>

        {open &&
          createPortal(
            <div
              className={cn(
                'z-[9999] w-[320px]',
                'rounded-md border border-telekinesis-neutral-800 bg-telekinesis-bg-secondary-dark text-telekinesis-neutral-10 shadow-lg',
                'animate-in fade-in-0 zoom-in-95',
              )}
              style={cardStyle}
              onMouseEnter={handleEnter}
              onMouseLeave={handleLeave}
            >
              {/* Header */}
              <div className="flex items-center justify-between gap-3 px-4 pb-3 pt-4">
                <div className="text-base font-semibold leading-tight">
                  {headerLabel}
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    setOpen(false);
                    setPinnedByClick(false);
                    setUpgradeOpen(true);
                  }}
                  className="h-8 rounded-full bg-telekinesis-neutral-10 px-4 text-xs font-medium text-telekinesis-bg-dark [@media(hover:hover)]:hover:bg-white [@media(hover:hover)]:hover:text-telekinesis-bg-dark"
                >
                  Upgrade
                </Button>
              </div>

              <div className="h-px bg-telekinesis-neutral-800" />

              {/* Credits breakdown */}
              <div className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap
                      className="h-4 w-4 text-telekinesis-neutral-10"
                      fill="currentColor"
                    />
                    <span className="text-sm font-medium">Credits</span>
                  </div>
                  <span className="text-sm font-semibold tabular-nums">
                    {formatFull(totalTokens)}
                  </span>
                </div>

                <div className="mt-2 space-y-1 pl-6 text-xs text-telekinesis-neutral-400">
                  {!isFree && (
                    <div className="flex items-center justify-between">
                      <span>Monthly credits</span>
                      <span className="tabular-nums">
                        {formatFull(subscriptionTokens)}
                      </span>
                    </div>
                  )}
                  {freeTokens > 0 && (
                    <div className="flex items-center justify-between">
                      <span>Daily free credits</span>
                      <span className="tabular-nums">
                        {formatFull(freeTokens)}
                      </span>
                    </div>
                  )}
                  {purchasedTokens > 0 && (
                    <div className="flex items-center justify-between">
                      <span>Add-on credits</span>
                      <span className="tabular-nums">
                        {formatFull(purchasedTokens)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="h-px bg-telekinesis-neutral-800" />

              {/* Footer link */}
              <Link
                to="/subscription"
                className="flex w-full items-center justify-between px-4 py-3 text-sm text-telekinesis-neutral-10 [@media(hover:hover)]:hover:bg-telekinesis-neutral-900"
              >
                <span>View plans</span>
                <span aria-hidden>→</span>
              </Link>
            </div>,
            document.body,
          )}
      </div>
    </>
  );
}
